import type { FeedTone } from "@rally/ui-kit";
import { say } from "../localization/say.ts";
import { calledBy } from "../me/called.ts";

export type Happening = {
  id: string;
  seq: string;
  kind: string;
  at: string;
  payload: Record<string, unknown>;
  preview: string | null;
  by: { account: string; handle: string | null; name: string | null; avatar: string | null };
  conversation: { id: string; title: string | null } | null;
  project: { id: string; name: string } | null;
};

type Reading = { tone: FeedTone; state: string | null; line: string };

function counted(payload: Record<string, unknown>): string {
  const did = payload.did;
  if (!Array.isArray(did) || did.length === 0) return say("happened.didSomeWork");
  const tally = new Map<string, number>();
  for (const one of did) {
    const tool = (one as { tool?: unknown }).tool;
    if (typeof tool !== "string") continue;
    tally.set(tool, (tally.get(tool) ?? 0) + 1);
  }
  const parts = [...tally.entries()].map(([tool, times]) =>
    times === 1 ? tool : say("happened.times")(tool, times),
  );
  return parts.length > 0 ? say("happened.ran")(parts.join(", ")) : say("happened.didSomeWork");
}

function howMany(payload: Record<string, unknown>, key: string): string {
  const held = payload[key];
  const many = Array.isArray(held) ? held.length : 0;
  return many === 1 ? say("happened.somebody") : say("happened.people")(many);
}

export function whatIsServed(payload: Record<string, unknown>): string {
  const name = said(payload, "repo") ?? said(payload, "app") ?? say("happened.anApp");
  const branch = said(payload, "branch");
  const commit = said(payload, "commit");
  return `${name}${branch ? ` ${branch}` : ""}${commit ? ` ${commit.slice(0, 7)}` : ""}`;
}

export function whatTheReleaseCarried(happening: Happening): string | null {
  if (happening.kind !== "serve.set") return null;
  const message = said(happening.payload, "commitMessage");
  return message ? trimmedToAWord(message) : null;
}

function shipped(payload: Record<string, unknown>): string {
  const repo = said(payload, "repo") ?? "it";
  const branch = said(payload, "branch");
  const target = said(payload, "target");
  return `${repo}${branch ? ` ${branch}` : ""}${target ? ` → ${target}` : ""}`;
}

function said(payload: Record<string, unknown>, key: string): string | null {
  const value = payload[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

const PREVIEW_CUT = 160;

function trimmedToAWord(preview: string): string {
  if (preview.length < PREVIEW_CUT) return preview;
  const cut = preview.lastIndexOf(" ", PREVIEW_CUT - 1);
  return `${preview.slice(0, cut > 40 ? cut : PREVIEW_CUT).replace(/[,;:.]+$/, "")}…`;
}

const READINGS: Record<string, (payload: Record<string, unknown>, preview: string | null) => Reading> = {
  "agent.delegated": (payload) => ({
    tone: "acting",
    state: "delegating",
    line: say("happened.delegated")(said(payload, "handle") ?? say("happened.somebody")),
  }),
  "agent.delegationReleased": (payload) => ({
    tone: "acting",
    state: "released",
    line: say("happened.released")(said(payload, "after") ?? say("happened.theOtherRun")),
  }),
  "agent.reportedAnEnd": (payload) => {
    const how = said(payload, "how");
    if (how === "broke") return { tone: "failed", state: "broke", line: say("happened.cameBackBroken") };
    return { tone: "answered", state: how ?? "reported", line: say("happened.reportedBack") };
  },
  "agent.nudged": () => ({ tone: "acting", state: "nudged", line: say("happened.nudged") }),
  "agent.steered": (payload) => ({
    tone: "acting",
    state: "steered",
    line: said(payload, "note") ?? say("happened.steered"),
  }),
  "agent.defined": () => ({ tone: "joined", state: null, line: say("happened.defined") }),
  "message.posted": (_payload, preview) => ({
    tone: "answered",
    state: null,
    line: preview === null ? say("happened.saidSomething") : trimmedToAWord(preview),
  }),
  "run.started": () => ({ tone: "acting", state: "thinking", line: say("happened.startedARun") }),
  "run.finished": () => ({ tone: "answered", state: null, line: say("happened.finishedItsRun") }),
  "run.stopped": () => ({ tone: "failed", state: "stopped", line: say("happened.wasStopped") }),
  "run.did": (payload) => ({ tone: "acting", state: null, line: counted(payload) }),
  "participant.added": (payload) => ({
    tone: "joined",
    state: null,
    line: say("happened.joinedAs")(said(payload, "role") ?? say("happened.aMember")),
  }),
  "conversation.created": (payload) => ({
    tone: "joined",
    state: null,
    line: say("happened.opened")(said(payload, "title") ?? say("happened.aConversation")),
  }),
  "conversation.appTargeted": () => ({ tone: "arrived", state: null, line: say("happened.pointedAnApp") }),
  "object.added": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.added")(said(payload, "name") ?? say("happened.aFile")),
  }),
  "message.mentioned": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.addressed")(howMany(payload, "mentioned")),
  }),
  "deploy.finished": (payload) => ({
    tone: "answered",
    state: null,
    line: say("happened.shipped")(shipped(payload)),
  }),
  "deploy.failed": (payload) => ({
    tone: "failed",
    state: "refused",
    line: say("happened.couldNotShip")(shipped(payload), said(payload, "why")),
  }),
  "deploy.skipped": (payload) => ({
    tone: "quiet",
    state: null,
    line: say("happened.leftWhereItWas")(shipped(payload)),
  }),
  "serve.set": (payload) => ({
    tone: said(payload, "state") === "running" ? "answered" : "quiet",
    state: said(payload, "state"),
    line:
      said(payload, "commitMessage") ??
      say("happened.isRunningAt")(
        whatIsServed(payload),
        said(payload, "state") ?? say("happened.set"),
        said(payload, "url"),
      ),
  }),
  "call.scheduled": () => ({ tone: "arrived", state: null, line: say("happened.scheduledACall") }),
  "call.started": () => ({ tone: "acting", state: "in a call", line: say("happened.startedACall") }),
  "call.joined": () => ({ tone: "joined", state: null, line: say("happened.joinedTheCall") }),
  "call.left": () => ({ tone: "quiet", state: null, line: say("happened.leftTheCall") }),
  "call.ended": () => ({ tone: "answered", state: null, line: say("happened.endedTheCall") }),
  "ship.set": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.ships")(
      said(payload, "repo") ?? say("happened.aRepository"),
      said(payload, "branch") ?? say("happened.itsBranch"),
      said(payload, "target") ?? say("happened.somewhere"),
    ),
  }),
  "app.created": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.madeTheApp")(said(payload, "slug") ?? said(payload, "name") ?? ""),
  }),
  "app.placed": () => ({ tone: "arrived", state: null, line: say("happened.putAnApp") }),
  "app.unplaced": () => ({ tone: "quiet", state: null, line: say("happened.tookAnApp") }),
  "app.repositoryAttached": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.attachedTheRepository")(said(payload, "repository") ?? ""),
  }),
  "app.repositoryTokenSet": () => ({
    tone: "arrived",
    state: null,
    line: say("happened.gaveRepositoryAccess"),
  }),
  "app.secretKept": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.keptTheSecret")(said(payload, "name") ?? ""),
  }),
  "app.secretForgotten": (payload) => ({
    tone: "quiet",
    state: null,
    line: say("happened.forgotTheSecret")(said(payload, "name") ?? ""),
  }),
  "app.environmentSet": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.saidWhereItLives")(said(payload, "name") ?? say("happened.anEnvironment")),
  }),
  "app.environmentRemoved": (payload) => ({
    tone: "quiet",
    state: null,
    line: say("happened.removedTheEnvironment")(said(payload, "name") ?? ""),
  }),
  "project.created": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.startedTheProject")(said(payload, "name") ?? ""),
  }),
  "project.joined": () => ({ tone: "joined", state: null, line: say("happened.joinedTheProject") }),
  "agent.created": (payload) => ({
    tone: "arrived",
    state: null,
    line: say("happened.madeTheAgent")(said(payload, "handle") ?? say("happened.somebody")),
  }),
  "agent.edited": () => ({ tone: "quiet", state: null, line: say("happened.changedAnAgent") }),
  "account.created": () => ({ tone: "joined", state: null, line: say("happened.joinedRally") }),
};

export function howToReadIt(happening: Happening): Reading {
  const reading = READINGS[happening.kind];
  if (reading) return reading(happening.payload, happening.preview);
  return { tone: "quiet", state: null, line: happening.kind.replace(/\./g, " ") };
}

export function whoDidIt(happening: Happening): string {
  return (
    calledBy({ handle: happening.by.handle ?? "", displayName: happening.by.name ?? null }) ??
    say("happened.somebody")
  );
}

export function whereItHappened(happening: Happening): string | undefined {
  if (whatTheReleaseCarried(happening)) {
    const where = [whatIsServed(happening.payload), happening.project?.name].filter(Boolean);
    return where.join(" · ");
  }
  const parts = [happening.conversation?.title, happening.project?.name].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.length > 0 ? parts.join(" · ") : undefined;
}
